(() => {
  'use strict';
  if(window.PinmeV4Products)return;
  const instances=new WeakMap();
  const modes=new WeakMap();
  const characterClickSuppression=new WeakMap();
  function characterClickIsSuppressed(section){
    const state=characterClickSuppression.get(section);
    if(!state)return false;
    if(state.held||performance.now()<state.until)return true;
    characterClickSuppression.delete(section);return false;
  }
  function prepareCharacterImages(section,current,busy){
    const items=[...section.querySelectorAll('[data-character-item]')],panels=[...section.querySelectorAll('[data-character-details-panel]')],decoded=new WeakMap();
    let near=false,timer=0,idle=0,disposed=false;
    const cancel=()=>{clearTimeout(timer);timer=0;if(idle){if(window.cancelIdleCallback)cancelIdleCallback(idle);else clearTimeout(idle);idle=0;}};
    const warm=()=>{
      idle=0;if(disposed||!near||busy())return;
      const index=current(),order=[index,(index+1)%items.length,(index+items.length-1)%items.length];
      [...new Set(order)].forEach(i=>[...items[i].querySelectorAll('img'),...(panels[i]?.querySelectorAll('img')||[])].forEach(image=>{
        const src=image.currentSrc||image.src;if(!src||decoded.get(image)===src)return;
        image.loading='eager';decoded.set(image,src);image.decode?.().catch(()=>{});
      }));
    };
    const schedule=()=>{cancel();if(disposed||!near||busy())return;timer=setTimeout(()=>{timer=0;idle=window.requestIdleCallback?requestIdleCallback(warm,{timeout:200}):setTimeout(warm,0);},32);};
    const observer='IntersectionObserver' in window?new IntersectionObserver(entries=>{near=entries.some(entry=>entry.isIntersecting);if(near)schedule();else cancel();},{rootMargin:'500px 0px'}):null;
    if(observer)observer.observe(section);else{near=true;schedule();}
    return{schedule,cancel,destroy:()=>{disposed=true;cancel();observer?.disconnect();}};
  }
  function measureCompactCharacter(section,stage,items){
    const width=stage.clientWidth,style=getComputedStyle(section);
    const mobileHeight=parseFloat(style.getPropertyValue('--char-height'))||480,originalMobile=matchMedia('(max-width:749px)').matches;
    const stageHeight=parseFloat(style.getPropertyValue('--character-stage-height'))||mobileHeight*Math.min(width,430)/390;
    const itemWidth=items[0]?.offsetWidth||width*.6;
    const shift=originalMobile?width*Math.min(.5384615,210/Math.max(width,390)):Math.max(0,Math.min(width*.3,width/2-itemWidth*.36-30));
    section.style.setProperty('--char-track-height',stageHeight+'px');
    section.style.setProperty('--character-mobile-scale',String(Math.max(1,width/430)));
    return{width,shift};
  }
  function initMobileCharacter(section){
    const stage=section.querySelector('[data-character-stage]'),details=section.querySelector('[data-character-details]');
    const scene=stage?.closest('.pinme-character3d__scene')||stage;
    const items=[...section.querySelectorAll('[data-character-item]')],panels=[...section.querySelectorAll('[data-character-details-panel]')];
    if(!stage||!items.length)return;
    const abort=new AbortController(),reduced=matchMedia('(prefers-reduced-motion:reduce)'),directTouch='TouchEvent' in window;
    const listen=(node,type,fn,options={})=>node?.addEventListener(type,fn,{...options,signal:abort.signal});
    const surfaces=panels.map(panel=>[panel]);
    let index=Math.max(0,Math.min(items.length-1,Number(section.dataset.characterIndex)||0)),gesture=null,suppressUntil=0,frame=0,transition=0,animations=[];
    let layout={width:0,shift:0},restPoses=[];
    const restingSurface={transform:'translate3d(0,0,0)',opacity:'1'};
    const images=prepareCharacterImages(section,()=>index,()=>!!gesture);
    section.dataset.mobileCharacterR7='true';
    const pose=node=>{const style=getComputedStyle(node);return{transform:style.transform,opacity:style.opacity};};
    const hasMotion=()=>animations.some(animation=>animation.playState==='running');
    const painted=(node,fallback,live)=>live&&!node.hidden?pose(node):{transform:node.style.transform||fallback.transform,opacity:node.style.opacity||fallback.opacity};
    const cancelAnimations=()=>{transition++;animations.forEach(animation=>animation.cancel());animations=[];};
    const cleanStyles=()=>[...items,...surfaces.flat()].forEach(node=>{node.style.removeProperty('transform');node.style.removeProperty('opacity');});
    const geometry=()=>{
      const shift=layout.shift;
      restPoses=items.map((item,i)=>{
        let offset=(i-index+items.length)%items.length;if(offset>items.length/2)offset-=items.length;
        const active=offset===0,visible=Math.abs(offset)<=1;
        item.hidden=!visible;item.style.setProperty('--char-x',offset*shift+'px');item.style.setProperty('--char-z',active?'80px':'-90px');
        item.style.setProperty('--char-scale',active?'1':'.84');item.style.setProperty('--char-opacity',active?'1':visible?'.7':'0');
        item.style.setProperty('--char-rotate',offset*-34+'deg');item.style.setProperty('--char-z-index',active?'10':'5');item.style.setProperty('--char-events',visible?'auto':'none');
        item.setAttribute('aria-hidden',String(!visible));item.setAttribute('role','button');item.tabIndex=visible?0:-1;
        item.setAttribute('aria-label',item.querySelector('[data-character-name]')?.textContent||'');item.setAttribute('aria-pressed',String(active));
        return{transform:'translate3d(0,0,0) translateX(calc(-50% + '+offset*shift+'px)) translateZ('+(active?80:-90)+'px) rotateY('+offset*-34+'deg) scale('+(active?1:.84)+')',opacity:active?'1':visible?'.7':'0'};
      });
      const name=items[index].querySelector('[data-character-name]')?.textContent||'',desc=items[index].querySelector('[data-character-desc]')?.textContent||'';
      const nameEl=section.querySelector('[data-character-active-name]'),descEl=section.querySelector('[data-character-active-desc]');
      if(nameEl)nameEl.textContent=name;if(descEl)descEl.textContent=desc;
      section.dataset.characterIndex=String(index);stage.setAttribute('aria-label',(index+1)+' / '+items.length+' — '+name);
      panels.forEach((panel,i)=>{panel.classList.toggle('is-active',i===index);panel.setAttribute('aria-hidden',String(i!==index));});
    };
    const resetPanels=()=>{panels.forEach((panel,i)=>panel.hidden=i!==index);};
    const measure=()=>{
      const width=stage.clientWidth;if(!width)return;
      layout=measureCompactCharacter(section,stage,items);
      if(details&&panels.length){
        details.style.removeProperty('height');
        section.classList.add('is-measuring-character');panels.forEach(panel=>panel.hidden=false);
        const panelHeight=Math.max(...panels.map(panel=>panel.offsetHeight));
        if(panelHeight)details.style.height=panelHeight+'px';
        resetPanels();section.classList.remove('is-measuring-character');
      }
    };
    const clearGesture=()=>{
      const previous=gesture;gesture=null;if(frame)cancelAnimationFrame(frame);frame=0;
      stage.classList.remove('is-dragging');details?.classList.remove('is-dragging');
      if(previous&&!previous.touch&&previous.scope.hasPointerCapture?.(previous.id))previous.scope.releasePointerCapture(previous.id);
      return previous;
    };
    const animate=(node,from,to,duration=240,delay=0)=>{
      const animation=node.animate([from,to],{duration,delay,easing:'cubic-bezier(.22,.8,.25,1)',fill:'both'});animations.push(animation);return animation;
    };
    const select=(next,direction=0,instant=false)=>{
      const old=index,live=hasMotion(),from=items.map((item,i)=>painted(item,restPoses[i],live)),surfacePoses=surfaces.map(group=>group.map(panel=>painted(panel,restingSurface,live))),visible=panels.map(panel=>!panel.hidden);
      clearGesture();cancelAnimations();cleanStyles();index=(next+items.length)%items.length;geometry();
      const token=transition,moving=!instant&&!reduced.matches;
      if(!moving){resetPanels();images.schedule();return;}
      panels.forEach((panel,i)=>panel.hidden=i!==index&&i!==old);
      const destination=restPoses;
      items.forEach((item,i)=>{if(!item.hidden)animate(item,from[i],destination[i],260);});
      const sign=direction<0?-1:1;
      if(old!==index){
        surfaces[old]?.forEach((tile,i)=>animate(tile,surfacePoses[old][i],{opacity:0,transform:'translate3d('+(-sign*14)+'px,0,0)'},190));
        surfaces[index]?.forEach((tile,i)=>animate(tile,visible[index]?surfacePoses[index][i]:{opacity:0,transform:'translate3d('+(sign*16)+'px,0,0)'},{opacity:1,transform:'translate3d(0,0,0)'},240,i*18));
      }else surfaces[index]?.forEach((tile,i)=>animate(tile,surfacePoses[index][i],{opacity:1,transform:'translate3d(0,0,0)'},180));
      Promise.allSettled(animations.map(animation=>animation.finished)).then(()=>{
        if(token!==transition)return;cancelAnimations();cleanStyles();resetPanels();images.schedule();
      });
    };
    const freeze=()=>{
      const live=hasMotion(),itemPoses=items.map((item,i)=>painted(item,restPoses[i],live)),surfacePoses=surfaces[index]?.map(panel=>painted(panel,restingSurface,live))||[];
      cancelAnimations();resetPanels();
      items.forEach((item,i)=>Object.assign(item.style,itemPoses[i]));surfaces[index]?.forEach((tile,i)=>Object.assign(tile.style,surfacePoses[i]));
      return{items:itemPoses,surfaces:surfacePoses};
    };
    const start=(event,scope)=>{
      if(event.button!==0||event.isPrimary===false||gesture||items.length<2||event.target.closest('a,button,input,video'))return;
      gesture={id:event.pointerId,touch:!!event.directTouch,scope,x:event.clientX,y:event.clientY,dx:0,dy:0,index,axis:null,scale:scope.getBoundingClientRect().width/Math.max(1,scope.clientWidth)};
      images.cancel();
      if(!gesture.touch&&event.pointerType!=='touch')event.preventDefault();
      if(!gesture.touch&&event.pointerType!=='touch')try{scope.setPointerCapture?.(event.pointerId);}catch(_){}
    };
    const paint=()=>{
      frame=0;if(!gesture?.poses)return;
      const travel=Math.max(-24,Math.min(24,gesture.dx*.15));
      items.forEach((item,i)=>{const p=gesture.poses.items[i];if(!item.hidden)item.style.transform=p.transform.startsWith('translate3d(')?p.transform.replace(/^translate3d\([^)]*\)/,'translate3d('+travel+'px,0,0)'):'translate3d('+travel+'px,0,0) '+(p.transform==='none'?'':p.transform);});
      surfaces[index]?.forEach((tile,i)=>{const p=gesture.poses.surfaces[i];tile.style.transform='translate3d('+travel+'px,0,0) '+(p.transform==='none'?'':p.transform);});
    };
    const move=event=>{
      if(!gesture||event.pointerId!==gesture.id)return;
      gesture.dx=(event.clientX-gesture.x)/gesture.scale;gesture.dy=(event.clientY-gesture.y)/gesture.scale;
      if(!gesture.axis){
        if(Math.max(Math.abs(gesture.dx),Math.abs(gesture.dy))<8)return;
        if(Math.abs(gesture.dy)>Math.abs(gesture.dx)*1.2)gesture.axis='vertical';
        else if(Math.abs(gesture.dx)>Math.abs(gesture.dy)*1.2){gesture.axis='horizontal';gesture.poses=freeze();stage.classList.add('is-dragging');details?.classList.add('is-dragging');if(!gesture.touch)gesture.scope.setPointerCapture?.(gesture.id);}
        else return;
      }
      if(gesture.axis!=='horizontal')return;event.preventDefault();suppressUntil=performance.now()+450;if(!frame)frame=requestAnimationFrame(paint);
    };
    const end=event=>{
      if(!gesture||event.pointerId!==gesture.id)return;
      const finished=gesture,dx=(event.clientX-finished.x)/finished.scale;
      if(finished.axis==='horizontal'){if(frame){cancelAnimationFrame(frame);frame=0;paint();}suppressUntil=performance.now()+450;}
      clearGesture();
      if(finished.axis==='horizontal')select(finished.index+(event.type==='pointerup'&&Math.abs(dx)>=35?(dx<0?1:-1):0),dx<0?1:-1);
      else images.schedule();
    };
    const scopes=[scene,details].filter(Boolean);
    scopes.forEach(scope=>{
      listen(scope,'pointerdown',event=>{if(!(directTouch&&event.pointerType==='touch'))start(event,scope);},{passive:false});
      listen(scope,'pointermove',event=>{if(!gesture?.touch)move(event);},{passive:false});
      listen(scope,'pointerup',event=>{if(!gesture?.touch)end(event);});listen(scope,'pointercancel',event=>{if(!gesture?.touch)end(event);});
      listen(scope,'lostpointercapture',event=>{if(!gesture?.touch&&event.target===scope)end(event);});listen(scope,'dragstart',event=>event.preventDefault(),{capture:true});
      if(directTouch){
        const point=(event,touch,type)=>({type,pointerId:touch.identifier,directTouch:true,button:0,isPrimary:true,clientX:touch.clientX,clientY:touch.clientY,target:event.target,preventDefault:()=>{if(event.cancelable)event.preventDefault();}});
        const cancel=()=>{suppressUntil=performance.now()+450;clearGesture();select(index,0,true);};
        listen(scope,'touchstart',event=>{if(event.touches.length!==1){cancel();return;}start(point(event,event.touches[0],'pointerdown'),scope);},{passive:true});
        listen(scope,'touchmove',event=>{if(event.touches.length!==1){cancel();return;}const touch=[...event.touches].find(t=>t.identifier===gesture?.id);if(touch)move(point(event,touch,'pointermove'));},{passive:false});
        const finish=event=>{const touch=[...event.changedTouches].find(t=>t.identifier===gesture?.id);if(touch)end(point(event,touch,event.type==='touchend'?'pointerup':'pointercancel'));};
        listen(scope,'touchend',finish);listen(scope,'touchcancel',finish);
      }
    });
    section.querySelectorAll('[data-character-prev],[data-character-next]').forEach(button=>{button.disabled=items.length<2;listen(button,'click',()=>select(index+(button.hasAttribute('data-character-prev')?-1:1),button.hasAttribute('data-character-prev')?-1:1));});
    listen(scene,'click',event=>{
      if(event.target.closest('a,button,input,video'))return;
      if(performance.now()<suppressUntil||characterClickIsSuppressed(section)){event.preventDefault();event.stopPropagation();return;}
      const candidates=items.map((item,i)=>({item,i,rect:item.getBoundingClientRect()})).filter(({item,rect})=>!item.hidden&&event.clientX>=rect.left&&event.clientX<=rect.right&&event.clientY>=rect.top&&event.clientY<=rect.bottom);
      candidates.sort((a,b)=>Math.abs(event.clientX-a.rect.left-a.rect.width/2)-Math.abs(event.clientX-b.rect.left-b.rect.width/2));
      const next=candidates[0]?.i??items.indexOf(event.target.closest('[data-character-item]'));if(next<0||next===index)return;
      let offset=(next-index+items.length)%items.length;if(offset>items.length/2)offset-=items.length;select(next,Math.sign(offset));
    },{capture:true});
    listen(stage,'keydown',event=>{
      const item=event.target.closest('[data-character-item]');
      if(item&&['Enter',' '].includes(event.key)){event.preventDefault();const next=items.indexOf(item);if(next!==index)select(next,next>index?1:-1);return;}
      if(!['ArrowLeft','ArrowRight','Home','End'].includes(event.key))return;event.preventDefault();
      if(event.key==='Home'||event.key==='End')select(event.key==='Home'?0:items.length-1);
      else select(index+(event.key==='ArrowRight'?1:-1),event.key==='ArrowRight'?1:-1);
    });
    listen(window,'pointerup',event=>{if(!gesture?.touch)end(event);});listen(window,'pointercancel',event=>{if(!gesture?.touch)end(event);});
    listen(window,'blur',()=>{clearGesture();select(index,0,true);});listen(reduced,'change',()=>select(index,0,true));
    let measuredWidth=stage.clientWidth,resizeFrame=0;
    const remeasure=()=>{if(resizeFrame)cancelAnimationFrame(resizeFrame);resizeFrame=requestAnimationFrame(()=>{resizeFrame=0;if(!abort.signal.aborted){clearGesture();measure();select(index,0,true);}});};
    const observer=new ResizeObserver(()=>{const width=stage.clientWidth;if(abort.signal.aborted||width===measuredWidth)return;measuredWidth=width;remeasure();});measure();geometry();resetPanels();observer.observe(stage);
    document.fonts?.ready.then(()=>{if(!abort.signal.aborted)remeasure();});
    instances.set(section,{select:id=>{const found=items.findIndex(item=>item.dataset.originalBlock===id||item.getAttribute('data-shopify-editor-block')?.includes(id));if(found>=0)select(found);},destroy:()=>{images.destroy();clearGesture();cancelAnimations();cleanStyles();resetPanels();details?.style.removeProperty('height');section.style.removeProperty('--char-track-height');section.style.removeProperty('--character-mobile-scale');abort.abort();observer.disconnect();if(resizeFrame)cancelAnimationFrame(resizeFrame);delete section.dataset.mobileCharacterR7;instances.delete(section);}});
  }
  function initTabletCharacter(section){
    if(instances.has(section))return;
    const stage=section.querySelector('[data-character-stage]');
    const scene=stage?.closest('.pinme-character3d__scene')||stage;
    const details=section.querySelector('[data-character-details]');
    const items=[...section.querySelectorAll('[data-character-item]')];
    const panels=[...section.querySelectorAll('[data-character-details-panel]')];
    if(!stage||!items.length)return;
    const abort=new AbortController();
    const directTouch='TouchEvent' in window;
    const touches=new Set();
    let index=Math.max(0,Math.min(items.length-1,Number(section.dataset.characterIndex)||0)),gesture=null,suppressClickUntil=0,dragFrame=0;
    const images=prepareCharacterImages(section,()=>index,()=>!!gesture);
    const listen=(target,type,callback,options={})=>target.addEventListener(type,callback,{...options,signal:abort.signal});
    const geometry=()=>{
      const {shift}=measureCompactCharacter(section,stage,items);
      items.forEach((item,i)=>{
        let offset=(i-index+items.length)%items.length;
        if(offset>items.length/2)offset-=items.length;
        const active=offset===0,visible=Math.abs(offset)<=1;
        item.hidden=!visible;
        item.style.setProperty('--char-x',(offset*shift)+'px');
        item.style.setProperty('--char-z',active?'80px':'-90px');
        item.style.setProperty('--char-scale',active?'1':'.84');
        item.style.setProperty('--char-opacity',active?'1':visible?'.7':'0');
        item.style.setProperty('--char-rotate',(offset*-34)+'deg');
        item.style.setProperty('--char-z-index',active?'10':'5');
        item.style.setProperty('--char-events',visible?'auto':'none');
        item.setAttribute('aria-hidden',String(!visible));
        item.setAttribute('role','button');
        item.tabIndex=visible?0:-1;
        item.setAttribute('aria-label',item.querySelector('[data-character-name]')?.textContent||'');
        item.setAttribute('aria-pressed',String(active));
      });
    };
    const clearGesture=()=>{
      const old=gesture;
      gesture=null;
      cancelAnimationFrame(dragFrame);dragFrame=0;
      stage.classList.remove('is-dragging');
      details?.classList.remove('is-dragging');
      items.forEach(item=>{item.style.removeProperty('transform');item.style.removeProperty('opacity');});
      panels.forEach(panel=>panel.style.removeProperty('transform'));
      if(old&&!old.touch&&old.scope.hasPointerCapture(old.id))old.scope.releasePointerCapture(old.id);
      return old;
    };
    const show=direction=>{
      geometry();
      const name=items[index].querySelector('[data-character-name]')?.textContent||'';
      const desc=items[index].querySelector('[data-character-desc]')?.textContent||'';
      const nameEl=section.querySelector('[data-character-active-name]');
      const descEl=section.querySelector('[data-character-active-desc]');
      if(nameEl)nameEl.textContent=name;
      if(descEl)descEl.textContent=desc;
      section.style.setProperty('--detail-enter-x',direction<0?'-16px':'16px');
      panels.forEach((panel,i)=>{
        panel.hidden=i!==index;
        panel.classList.toggle('is-active',i===index);
        panel.setAttribute('aria-hidden',String(i!==index));
      });
      section.dataset.characterIndex=String(index);
      stage.setAttribute('aria-label',(index+1)+' / '+items.length+' — '+name);
      images.schedule();
    };
    const select=(next,direction=0)=>{
      clearGesture();
      index=(next+items.length)%items.length;
      show(direction);
    };
    const go=direction=>select(index+direction,direction);
    section.querySelectorAll('[data-character-prev],[data-character-next]').forEach(button=>{
      button.disabled=items.length<2;
      listen(button,'click',()=>go(button.hasAttribute('data-character-prev')?-1:1));
    });
    listen(stage,'keydown',event=>{
      if((event.key==='Enter'||event.key===' ')&&event.target.closest('[data-character-item]')){
        event.preventDefault();
        const next=items.indexOf(event.target.closest('[data-character-item]'));
        if(next>=0&&next!==index)select(next,next>index?1:-1);
        return;
      }
      if(!['ArrowLeft','ArrowRight','Home','End'].includes(event.key))return;
      event.preventDefault();
      if(event.key==='Home'||event.key==='End')select(event.key==='Home'?0:items.length-1);
      else go(event.key==='ArrowRight'?1:-1);
    });
    listen(scene,'click',event=>{
      if(event.target.closest('a,button,input,video'))return;
      if(performance.now()<suppressClickUntil||characterClickIsSuppressed(section)){event.preventDefault();event.stopPropagation();return;}
      // Side figures sit behind the stage in 3D space. Resolve their painted bounds,
      // rather than relying on the browser's flat hit target through transparent pixels.
      const candidates=items.map((item,i)=>({item,i,rect:item.getBoundingClientRect()}))
        .filter(({item,rect})=>!item.hidden&&event.clientX>=rect.left&&event.clientX<=rect.right&&event.clientY>=rect.top&&event.clientY<=rect.bottom);
      candidates.sort((a,b)=>Math.abs(event.clientX-a.rect.left-a.rect.width/2)-Math.abs(event.clientX-b.rect.left-b.rect.width/2));
      const next=candidates[0]?.i??items.indexOf(event.target.closest('[data-character-item]'));
      if(next<0||next===index)return;
      let offset=(next-index+items.length)%items.length;if(offset>items.length/2)offset-=items.length;
      select(next,Math.sign(offset));
    },{capture:true});
    const scopes=[scene,details].filter(Boolean);
    const end=event=>{
      if(event.pointerType==='touch')touches.delete(event.pointerId);
      if(!gesture||event.pointerId!==gesture.id)return;
      const complete=gesture;
      const dx=(event.clientX-complete.x)/complete.scale;
      const dy=(event.clientY-complete.y)/complete.scale;
      const moved=complete.axis==='horizontal'||complete.axis===null&&Math.abs(dx)>8&&Math.abs(dx)>Math.abs(dy)*1.2;
      if(Math.max(Math.abs(dx),Math.abs((event.clientY-complete.y)/complete.scale))>=8)suppressClickUntil=performance.now()+450;
      clearGesture();
      if(event.type==='pointerup'&&moved&&Math.abs(dx)>35)select(complete.index+(dx<0?1:-1),dx<0?1:-1);
      else{index=complete.index;show(0);}
    };
    scopes.forEach(scope=>{
      const start=event=>{
        if(event.pointerType==='touch'){
          touches.add(event.pointerId);
          if(touches.size>1){clearGesture();show(0);return;}
        }
        if(event.button!==0||event.isPrimary===false||items.length<2||gesture)return;
        if(event.target.closest('a,button,input,video'))return;
        const width=scope.clientWidth,rect=scope.getBoundingClientRect();
        gesture={id:event.pointerId,touch:event.directTouch===true,scope,index,x:event.clientX,y:event.clientY,axis:null,scale:rect.width/(width||1),width,poses:null,travel:0};
        images.cancel();
        if(!gesture.touch&&event.pointerType!=='touch')event.preventDefault();
        if(!gesture.touch&&event.pointerType!=='touch')try{scope.setPointerCapture?.(event.pointerId);}catch(_){}
      };
      const move=event=>{
        if(!gesture||gesture.id!==event.pointerId||gesture.scope!==scope)return;
        const dx=(event.clientX-gesture.x)/gesture.scale,dy=(event.clientY-gesture.y)/gesture.scale;
        if(!gesture.axis){
          if(Math.max(Math.abs(dx),Math.abs(dy))<8)return;
          gesture.axis=Math.abs(dx)>Math.abs(dy)*1.2?'horizontal':Math.abs(dy)>Math.abs(dx)*1.2?'vertical':null;
          if(gesture.axis==='horizontal'){
            // Freeze the currently painted pose, so a new drag can interrupt an animation without jumping.
            gesture.poses=items.map(item=>({transform:getComputedStyle(item).transform,opacity:getComputedStyle(item).opacity}));
            (scope===details?details:stage).classList.add('is-dragging');
            if(!gesture.touch)scope.setPointerCapture(event.pointerId);
          }
        }
        if(gesture.axis!=='horizontal')return;
        event.preventDefault();
        gesture.travel=Math.max(-24,Math.min(24,dx*.15));
        if(!dragFrame)dragFrame=requestAnimationFrame(()=>{
          dragFrame=0;if(!gesture||gesture.scope!==scope||gesture.axis!=='horizontal')return;
          const travel=gesture.travel;
          if(scope===scene){
            items.forEach((item,i)=>{
              const pose=gesture.poses[i];if(item.hidden)return;
              item.style.transform='translate3d('+travel+'px,0,0) '+(pose.transform==='none'?'':pose.transform);
              item.style.opacity=pose.opacity;
            });
          }else if(panels[index])panels[index].style.transform='translate3d('+travel+'px,0,0)';
        });
      };
      listen(scope,'pointerdown',event=>{if(!gesture?.touch)start(event);},{passive:false});
      listen(scope,'pointermove',event=>{if(!gesture?.touch)move(event);},{passive:false});
      listen(scope,'pointerup',event=>{if(!gesture?.touch)end(event);});
      listen(scope,'pointercancel',event=>{if(!gesture?.touch)end(event);});
      listen(scope,'lostpointercapture',event=>{if(!gesture?.touch&&event.target===scope)end(event);});
      listen(scope,'dragstart',event=>event.preventDefault(),{capture:true});
      if(directTouch){
        const point=(event,touch,type)=>({type,pointerId:touch.identifier,pointerType:'touch',directTouch:true,button:0,isPrimary:true,clientX:touch.clientX,clientY:touch.clientY,target:event.target,preventDefault:()=>{if(event.cancelable)event.preventDefault();}});
        listen(scope,'touchstart',event=>{
          if(event.touches.length!==1){suppressClickUntil=performance.now()+450;touches.clear();clearGesture();show(0);return;}
          if(gesture&&!gesture.touch)clearGesture();
          touches.clear();start(point(event,event.touches[0],'pointerdown'));
        },{passive:true});
        listen(scope,'touchmove',event=>{
          if(event.touches.length!==1){touches.clear();clearGesture();show(0);return;}
          const touch=[...event.touches].find(t=>t.identifier===gesture?.id);
          if(touch)move(point(event,touch,'pointermove'));
        },{passive:false});
        const finishTouch=event=>{
          const touch=[...event.changedTouches].find(t=>gesture?.touch&&t.identifier===gesture.id);
          if(touch)end(point(event,touch,event.type==='touchend'?'pointerup':'pointercancel'));
          touches.clear();
        };
        listen(scope,'touchend',finishTouch);listen(scope,'touchcancel',finishTouch);
      }
    });
    // Release touch bookkeeping even when a second finger ends outside the interaction surface.
    listen(window,'pointerup',event=>{if(gesture?.touch)return;touches.delete(event.pointerId);if(gesture?.id===event.pointerId)end(event);});
    listen(window,'pointercancel',event=>{if(gesture?.touch)return;touches.delete(event.pointerId);if(gesture?.id===event.pointerId)end(event);});
    listen(window,'blur',()=>{touches.clear();clearGesture();show(0);});
    let measuredWidth=stage.clientWidth,resizeFrame=0;
    const observer=new ResizeObserver(()=>{const width=stage.clientWidth;if(abort.signal.aborted||width===measuredWidth)return;measuredWidth=width;if(resizeFrame)cancelAnimationFrame(resizeFrame);resizeFrame=requestAnimationFrame(()=>{resizeFrame=0;if(!abort.signal.aborted){if(gesture)clearGesture();geometry();}});});
    show(0);
    observer.observe(stage);
    instances.set(section,{
      select:id=>{
        const found=items.findIndex(item=>item.dataset.originalBlock===id||item.getAttribute('data-shopify-editor-block')?.includes(id));
        if(found>=0)select(found);
      },
      destroy:()=>{images.destroy();clearGesture();details?.style.removeProperty('height');section.style.removeProperty('--char-track-height');section.style.removeProperty('--character-mobile-scale');abort.abort();observer.disconnect();if(resizeFrame)cancelAnimationFrame(resizeFrame);instances.delete(section);}
    });
  }
  function initFreeCharacter(section){
    const stage=section.querySelector('[data-character-stage]'),details=section.querySelector('[data-character-details]');
    const scene=stage?.closest('.pinme-character3d__scene')||stage;
    const items=[...section.querySelectorAll('[data-character-item]')],panels=[...section.querySelectorAll('[data-character-details-panel]')];
    if(!stage||!items.length)return;
    const abort=new AbortController(),reduced=matchMedia('(prefers-reduced-motion:reduce)'),count=items.length;
    const listen=(node,type,fn,options={})=>node?.addEventListener(type,fn,{...options,signal:abort.signal});
    const modulo=value=>((value%count)+count)%count;
    const names=items.map(item=>item.querySelector('[data-character-name]')?.textContent||'');
    const descriptions=items.map(item=>item.querySelector('[data-character-desc]')?.textContent||'');
    const nameEl=section.querySelector('[data-character-active-name]'),descEl=section.querySelector('[data-character-active-desc]');
    const saved=items.map(item=>({styles:['transform','opacity','--char-z-index','--char-events'].map(key=>[key,item.style.getPropertyValue(key)]),hidden:item.hidden,attributes:['role','tabindex','aria-hidden','aria-label','aria-pressed'].map(key=>[key,item.getAttribute(key)])}));
    const trackHeight=section.style.getPropertyValue('--char-track-height');
    let position=Math.max(0,Math.min(count-1,Number(section.dataset.characterIndex)||0)),requested=position,index=-1,width=stage.clientWidth,pixelsPerItem=Math.max(180,width*.62);
    let gesture=null,motion=null,dirty=false,frame=0,resizeFrame=0,wheelTimer=0,wheelActive=false,suppressUntil=0;
    const images=prepareCharacterImages(section,()=>Math.max(0,index),()=>!!gesture||!!motion||dirty||wheelActive);
    section.dataset.freeCharacter='true';
    section.style.setProperty('--char-track-height','440px');
    items.forEach((item,i)=>{item.hidden=false;item.style.setProperty('--char-z-index','1');item.style.setProperty('--char-events','auto');item.setAttribute('role','button');item.setAttribute('aria-label',names[i]);item.setAttribute('aria-hidden','false');});
    const showDetails=next=>{
      if(next===index)return;index=next;
      if(nameEl)nameEl.textContent=names[index];if(descEl)descEl.textContent=descriptions[index];
      section.dataset.characterIndex=String(index);stage.setAttribute('aria-label',(index+1)+' / '+count+' — '+names[index]);
      items.forEach((item,i)=>{item.tabIndex=i===index?0:-1;item.setAttribute('aria-pressed',String(i===index));});
      panels.forEach((panel,i)=>{panel.hidden=i!==index;panel.classList.toggle('is-active',i===index);panel.setAttribute('aria-hidden',String(i!==index));});
    };
    const paint=value=>{
      position=value;
      const phase=modulo(value),radius=width*.31;
      items.forEach((item,i)=>{
        const angle=(i-phase)*Math.PI*2/count,depth=(Math.cos(angle)+1)/2;
        const x=Math.sin(angle)*radius,z=-120+200*depth,scale=.63+.49*Math.pow(depth,1.2);
        item.style.transform='translate3d(calc(-50% + '+x.toFixed(3)+'px),0,'+z.toFixed(3)+'px) scale('+scale.toFixed(5)+')';
        item.style.opacity=String(.25+.75*depth);
      });
      showDetails(modulo(Math.round(value)));
    };
    const schedule=()=>{if(!frame&&!abort.signal.aborted)frame=requestAnimationFrame(tick);};
    const tick=time=>{
      frame=0;if(abort.signal.aborted)return;
      if(dirty){dirty=false;paint(requested);}
      if(motion?.type==='tween'){
        if(motion.started===null)motion.started=time;
        const progress=Math.min(1,(time-motion.started)/280),ease=1-Math.pow(1-progress,3);
        paint(motion.from+(motion.to-motion.from)*ease);
        if(progress===1)motion=null;
      }
      if(motion||dirty)schedule();else if(!gesture&&!wheelActive)images.schedule();
    };
    const releaseCapture=old=>{if(old?.scope.hasPointerCapture?.(old.id))try{old.scope.releasePointerCapture(old.id);}catch(_){};};
    const clearGesture=()=>{const old=gesture;gesture=null;stage.classList.remove('is-dragging');details?.classList.remove('is-dragging');releaseCapture(old);return old;};
    const stop=()=>{
      if(frame)cancelAnimationFrame(frame);frame=0;motion=null;dirty=false;requested=position;
      clearTimeout(wheelTimer);wheelTimer=0;wheelActive=false;clearGesture();images.cancel();
    };
    const select=next=>{
      stop();
      if(reduced.matches){requested=next;dirty=true;}
      else motion={type:'tween',from:position,to:next,started:null};
      schedule();
    };
    const settle=()=>{
      const current=dirty?requested:position;
      if(dirty){dirty=false;paint(current);}
      select(Math.round(current));
    };
    const selectItem=next=>{let offset=modulo(next-modulo(position));if(offset>count/2)offset-=count;select(position+offset);};
    const go=direction=>select(Math.round(motion?.type==='tween'?motion.to:position)+direction);
    const start=(event,scope)=>{
      if(event.pointerType==='touch'&&event.isPrimary===false){settle();return;}
      if(event.button!==0||event.isPrimary===false||gesture||count<2||event.target.closest('a,button,input,textarea,select,video'))return;
      stop();
      const rect=scope.getBoundingClientRect(),scale=rect.width/Math.max(1,scope.clientWidth);
      gesture={id:event.pointerId,scope,start:position,x:event.clientX,y:event.clientY,scale,axis:null,dx:0};
      if(event.pointerType!=='touch')event.preventDefault();
      try{scope.setPointerCapture?.(event.pointerId);}catch(_){}
    };
    const move=event=>{
      if(!gesture||gesture.id!==event.pointerId)return;
      const dx=(event.clientX-gesture.x)/gesture.scale,dy=(event.clientY-gesture.y)/gesture.scale;
      if(!gesture.axis){
        if(Math.abs(dy)>Math.abs(dx)*1.2&&Math.abs(dy)>4)gesture.axis='vertical';
        else if(Math.abs(dx)>Math.abs(dy)*1.2&&Math.abs(dx)>0)gesture.axis='horizontal';
        else return;
      }
      if(gesture.axis!=='horizontal')return;
      event.preventDefault();gesture.dx=dx;stage.classList.add('is-dragging');if(gesture.scope===details)details.classList.add('is-dragging');
      if(Math.abs(dx)>4)suppressUntil=performance.now()+450;
      requested=gesture.start-dx/pixelsPerItem;dirty=true;
      schedule();
    };
    const end=event=>{
      if(!gesture||gesture.id!==event.pointerId)return;
      if(event.type==='pointerup')move(event);
      const old=clearGesture();
      if(old.axis==='horizontal'&&Math.abs(old.dx)>4)suppressUntil=performance.now()+450;
      settle();
    };
    [scene,details].filter(Boolean).forEach(scope=>{
      listen(scope,'pointerdown',event=>start(event,scope),{passive:false});
      listen(scope,'pointermove',move,{passive:false});listen(scope,'pointerup',end);listen(scope,'pointercancel',end);
      listen(scope,'lostpointercapture',event=>{if(gesture&&gesture.id===event.pointerId&&event.target===scope){clearGesture();settle();}});
      listen(scope,'dragstart',event=>event.preventDefault(),{capture:true});
      listen(scope,'wheel',event=>{
        if(count<2||gesture||event.ctrlKey||event.target.closest('input,textarea,select,video'))return;
        let delta=event.deltaX;
        if(event.shiftKey&&Math.abs(event.deltaY)>Math.abs(delta))delta=event.deltaY;
        else if(Math.abs(delta)<=Math.abs(event.deltaY)*1.15)return;
        if(!delta)return;event.preventDefault();
        if(!wheelActive){stop();wheelActive=true;}
        clearTimeout(wheelTimer);images.cancel();
        const unit=event.deltaMode===1?16:event.deltaMode===2?width:1;
        requested+=delta*unit/pixelsPerItem;dirty=true;suppressUntil=performance.now()+450;schedule();
        wheelTimer=setTimeout(()=>{wheelTimer=0;wheelActive=false;settle();},140);
      },{passive:false});
    });
    listen(scene,'click',event=>{
      if(event.target.closest('a,button,input,textarea,select,video'))return;
      if(performance.now()<suppressUntil||characterClickIsSuppressed(section)){event.preventDefault();event.stopPropagation();return;}
      const candidates=items.map((item,i)=>({i,rect:item.getBoundingClientRect()})).filter(({rect})=>event.clientX>=rect.left&&event.clientX<=rect.right&&event.clientY>=rect.top&&event.clientY<=rect.bottom);
      candidates.sort((a,b)=>Math.abs(event.clientX-a.rect.left-a.rect.width/2)-Math.abs(event.clientX-b.rect.left-b.rect.width/2));
      const next=candidates[0]?.i??items.indexOf(event.target.closest('[data-character-item]'));if(next>=0)selectItem(next);
    },{capture:true});
    section.querySelectorAll('[data-character-prev],[data-character-next]').forEach(button=>{button.disabled=count<2;listen(button,'click',()=>go(button.hasAttribute('data-character-prev')?-1:1));});
    listen(stage,'keydown',event=>{
      const item=event.target.closest('[data-character-item]');
      if(item&&['Enter',' '].includes(event.key)){event.preventDefault();selectItem(items.indexOf(item));return;}
      if(event.key==='Escape'){settle();return;}
      if(!['ArrowLeft','ArrowRight','Home','End'].includes(event.key))return;event.preventDefault();
      if(event.key==='Home'||event.key==='End')selectItem(event.key==='Home'?0:count-1);else go(event.key==='ArrowRight'?1:-1);
    });
    listen(window,'pointerup',end);listen(window,'pointercancel',end);
    listen(window,'blur',settle);listen(window,'pagehide',settle);
    listen(document,'visibilitychange',()=>{if(document.hidden)settle();else images.schedule();});
    listen(reduced,'change',()=>{
      if(!reduced.matches)return;
      const next=motion?.type==='tween'?motion.to:Math.round(dirty?requested:position);
      stop();
      if(next!==position){requested=next;dirty=true;schedule();}else images.schedule();
    });
    const observer=new ResizeObserver(entries=>{
      if(abort.signal.aborted)return;
      const next=entries[0]?.contentRect.width;if(!next||Math.abs(next-width)<.5)return;
      stop();if(resizeFrame)cancelAnimationFrame(resizeFrame);
      resizeFrame=requestAnimationFrame(()=>{resizeFrame=0;if(abort.signal.aborted)return;width=stage.clientWidth;pixelsPerItem=Math.max(180,width*.62);settle();});
    });
    observer.observe(stage);paint(position);
    instances.set(section,{select:id=>{const found=items.findIndex(item=>item.dataset.originalBlock===id||item.getAttribute('data-shopify-editor-block')?.includes(id));if(found>=0)selectItem(found);},destroy:()=>{
      stop();images.destroy();abort.abort();observer.disconnect();if(resizeFrame)cancelAnimationFrame(resizeFrame);
      items.forEach((item,i)=>{saved[i].styles.forEach(([key,value])=>value?item.style.setProperty(key,value):item.style.removeProperty(key));item.hidden=saved[i].hidden;saved[i].attributes.forEach(([key,value])=>value===null?item.removeAttribute(key):item.setAttribute(key,value));});
      if(trackHeight)section.style.setProperty('--char-track-height',trackHeight);else section.style.removeProperty('--char-track-height');
      delete section.dataset.freeCharacter;instances.delete(section);
    }});
  }
  function initCharacter(section){
    if(modes.has(section))return;
    section.querySelectorAll('[data-character-stage] img,[data-character-details] img').forEach(image=>{image.draggable=false;});
    const phone=matchMedia('(max-width:749px)'),desktop=matchMedia('(min-width:1080px)');
    let activeMode='';
    const releaseSuppression=()=>{const state=characterClickSuppression.get(section);if(state?.held){state.held=false;state.until=performance.now()+450;}};
    const releaseEvents=['pointerup','pointercancel','touchend','touchcancel','blur','pagehide'];
    const apply=()=>{const next=phone.matches?'phone':desktop.matches?'desktop':'tablet';if(next===activeMode)return;if(section.querySelector('.pinme-character3d__stage.is-dragging,.pinme-character3d__details.is-dragging'))characterClickSuppression.set(section,{held:true,until:performance.now()+450});instances.get(section)?.destroy();activeMode=next;if(next==='phone')initMobileCharacter(section);else if(next==='desktop')initFreeCharacter(section);else initTabletCharacter(section);};
    phone.addEventListener('change',apply);desktop.addEventListener('change',apply);
    releaseEvents.forEach(type=>window.addEventListener(type,releaseSuppression,true));
    modes.set(section,{destroy:()=>{phone.removeEventListener('change',apply);desktop.removeEventListener('change',apply);releaseEvents.forEach(type=>window.removeEventListener(type,releaseSuppression,true));instances.get(section)?.destroy();characterClickSuppression.delete(section);modes.delete(section);}});apply();
  }
  function characters(scope){return [...(scope.matches?.('[data-v4-character]')?[scope]:[]),...scope.querySelectorAll('[data-v4-character]')];}
  function init(scope=document){characters(scope).forEach(initCharacter);window.PinmeStudioMedia?.init(scope);}
  window.PinmeV4Products={init};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init(),{once:true});else init();
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload',event=>characters(event.target).forEach(section=>modes.get(section)?.destroy()));
  document.addEventListener('shopify:block:select',event=>{const section=event.target.closest('[data-v4-character]');if(section)instances.get(section)?.select(event.detail.blockId);});
})();
